import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import './styles.css'

import { Box, Center, MantineProvider } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { Notifications } from '@mantine/notifications'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode, useState, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'

import { ApiError } from './api/client'
import { ApiProvider } from './api/ApiContext'
import { AuthApi } from './auth/authApi'
import { AuthProvider, useAuth } from './auth/AuthContext'
import { AuthManager } from './auth/authManager'
import { browserStorage, removeLegacyAuthState } from './auth/session'
import { SignInScreen } from './auth/SignInScreen'
import { StatusBanner } from './components/StatusBanner'
import { loadConfig, type AppConfig } from './config'
import { CookiesPage } from './features/legal/CookiesPage'
import { DeleteAccountPage } from './features/legal/DeleteAccountPage'
import { PrivacyPage } from './features/legal/PrivacyPage'
import { TermsPage } from './features/legal/TermsPage'
import { router } from './router'
import { cssVariablesResolver, theme } from './theme'

/**
 * Public legal pages (UX spec: no sign-in anywhere else does this): plain paths, matched before config or auth
 * load at all, so they still work when config.json is missing or the session is broken.
 */
const PUBLIC_LEGAL_PAGES: Record<string, () => ReactNode> = {
  '/privacy': () => <PrivacyPage />,
  '/terms': () => <TermsPage />,
  '/cookies': () => <CookiesPage />,
  '/delete-account': () => <DeleteAccountPage />,
}

/**
 * Toasts sit where the thumb is on a phone and out of the way on a desktop (UX spec §4.5). `mt-notifications`
 * (styles.css) lifts the bottom-centre position clear of the FAB and bottom nav on compact windows — the FAB must
 * never cover anything, owner device test, 24 Oct 2026 — rather than sit at Mantine's default screen-edge offset.
 */
function ResponsiveNotifications() {
  const wide = useMediaQuery('(min-width: 48em)')
  return <Notifications className="mt-notifications" position={wide ? 'top-right' : 'bottom-center'} />
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
          return failureCount < 2
        },
      },
    },
  })
}

function Shell(props: { children: ReactNode }) {
  return (
    <StrictMode>
      <MantineProvider theme={theme} cssVariablesResolver={cssVariablesResolver} defaultColorScheme="auto">
        <ResponsiveNotifications />
        {props.children}
      </MantineProvider>
    </StrictMode>
  )
}

function SignedInApp(props: { config: AppConfig }) {
  const [queryClient] = useState(createQueryClient)
  return (
    <ApiProvider config={props.config}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ApiProvider>
  )
}

/** The sign-in screen until there is a session; keyed by user so each account starts with an empty cache. */
function App(props: { config: AppConfig }) {
  const { user } = useAuth()
  return user ? <SignedInApp key={user.id} config={props.config} /> : <SignInScreen />
}

async function start(root: ReturnType<typeof createRoot>) {
  const legalPage = PUBLIC_LEGAL_PAGES[window.location.pathname]
  if (legalPage) {
    root.render(<Shell>{legalPage()}</Shell>)
    return
  }

  let config: AppConfig
  try {
    config = await loadConfig()
  } catch (error) {
    root.render(
      <Shell>
        <Center h="100vh" p="md">
          <Box maw={480}>
            <StatusBanner tone="error" title="Configuration missing">
              {(error as Error).message}. Copy public/config.example.json to public/config.json for local development.
            </StatusBanner>
          </Box>
        </Center>
      </Shell>,
    )
    return
  }

  removeLegacyAuthState(browserStorage())
  const manager = new AuthManager({ api: new AuthApi(config.apiUrl) })
  root.render(
    <Shell>
      <AuthProvider manager={manager}>
        <App config={config} />
      </AuthProvider>
    </Shell>,
  )
}

const container = document.getElementById('root')
if (!container) throw new Error('missing #root element')
void start(createRoot(container))
